<template>
<Header></Header>
<Navi :isDarkMode="isDarkMode" @change-theme="switchTheme"></Navi>
<router-view/>
<ConsentBanner v-if="showConsentBanner" :consentStatus="consentStatus" @select-consent="setConsent"></ConsentBanner>
<Footer @edit-consent="editConsent"></Footer>
</template>

<script lang="ts">
import { defineComponent, ref, provide, onMounted } from 'vue';
import Header from '@/components/Header.vue';
import Navi from '@/components/Navi.vue';
import ConsentBanner from '@/components/ConsentBanner.vue';
import Footer from '@/components/Footer.vue';


type ConsentStatus = 'accepted' | 'rejected' | null

interface Consent {
  status: ConsentStatus
  date: string
}

const CONSENT_KEY = 'userConsent';
const META_THEME_COLOR_DARK = '#3a3a3a';
const META_THEME_COLOR_LIGHT = '#6599ff';


export default defineComponent({
  name: 'App',

  components: {
    Header,
    Navi,
    ConsentBanner,
    Footer
  },
  
  setup() {
    const consentStatus = ref<ConsentStatus>(null);
    const showConsentBanner = ref(false);

    const isDarkMode = ref(false);


    provide('consentStatus', consentStatus);

    onMounted(() => {
      const consentStored = localStorage.getItem(CONSENT_KEY);
      if(consentStored){
        try{
          const consentParsed = JSON.parse(consentStored) as Consent;
          const dateConsent = new Date(consentParsed.date);
          const dateNow = new Date();
          const dateExpire = new Date(dateConsent);
          dateExpire.setMonth(dateExpire.getMonth() + 3);

          if(dateNow < dateExpire){
            consentStatus.value = consentParsed.status;
            showConsentBanner.value = false;
          }else{
            localStorage.removeItem(CONSENT_KEY);
            consentStatus.value = null;
            showConsentBanner.value = true;
          }
        }catch(e){
          consentStatus.value = null;
          showConsentBanner.value = true;
        }
      }else{
        consentStatus.value = null;
        showConsentBanner.value = true;
      }


      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      isDarkMode.value = prefersDark;
      document.documentElement.setAttribute('data-color-theme', prefersDark ? 'dark' : 'light');
      document.querySelector('meta[name="theme-color"]')?.setAttribute("content", prefersDark ? META_THEME_COLOR_DARK : META_THEME_COLOR_LIGHT);

      const toggleBtn = document.getElementById('toggle_btn');
      if(prefersDark && toggleBtn){
        toggleBtn.classList.add('on');
      }
    });

    const setConsent = (status: ConsentStatus) => {
      if(status === 'accepted' || status === 'rejected'){
        localStorage.setItem(CONSENT_KEY, JSON.stringify({status, date: new Date().toISOString()}));
        consentStatus.value = status;
        showConsentBanner.value = false;
      }
    };

    const editConsent = () => {
      showConsentBanner.value = true;
    };

    const switchTheme = (isDarkModeEnabled: boolean) => {
      isDarkMode.value = isDarkModeEnabled;
      document.documentElement.setAttribute('data-color-theme', isDarkModeEnabled ? 'dark' : 'light');

      if(isDarkModeEnabled){
        document.querySelector('meta[name="theme-color"]')?.setAttribute("content", "#3a3a3a");
      }else{
        document.querySelector('meta[name="theme-color"]')?.setAttribute("content", "#6599ff");
      }
    };

    return {
      consentStatus,
      showConsentBanner,
      isDarkMode,

      setConsent,
      editConsent,
      switchTheme
    };
  }
});
</script>
